import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Calendar, Download } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../api';
import { API_BASE_URL, FILE_BASE_URL } from '../constants';

const JobDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isCreateMode = !id || id === 'create';

    const [formData, setFormData] = useState({
        title: '',
        company: '',
        field: 'Kỹ thuật',
        location: 'Hà Nội',
        deadline: new Date(),
        salary: '',
        salaryTo: '',
        quantity: 1,
        description: '',
        requirements: '',
        status: 'OPEN',
        education: '',
        experience: '',
        position: '',
        type: 'Toàn thời gian',
        banner: null
    });

    const [categories, setCategories] = useState({
        JOB_FIELD: [],
        LOCATION: [],
        JOB_TYPE: [],
        POSITION: [],
        EDUCATION: [],
        EXPERIENCE: []
    });

    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/categories');
                const allCategories = response.data;
                const grouped = {
                    JOB_FIELD: [],
                    LOCATION: [],
                    JOB_TYPE: [],
                    POSITION: [],
                    EDUCATION: [],
                    EXPERIENCE: []
                };
                allCategories.forEach(cat => {
                    if (grouped[cat.type] && cat.status) {
                        grouped[cat.type].push(cat);
                    }
                });
                setCategories(grouped);
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (!isCreateMode && id) {
            const fetchJob = async () => {
                try {
                    const response = await api.get(`/jobs/${id}`);
                    const jobData = response.data;
                    if (jobData.deadline) {
                        jobData.deadline = new Date(jobData.deadline);
                    }
                    setFormData(jobData);

                    // Fetch applicants
                    const appResponse = await api.get(`/applications/job/${id}`);
                    setApplicants(appResponse.data);
                } catch (error) {
                    console.error("Error fetching job or applicants:", error);
                }
            };
            fetchJob();
        }
    }, [id, isCreateMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            deadline: date
        }));
    };

    const handleDescriptionChange = (content) => {
        setFormData(prev => ({
            ...prev,
            description: content
        }));
    };

    const handleRequirementsChange = (content) => {
        setFormData(prev => ({
            ...prev,
            requirements: content
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({
                ...prev,
                banner: e.target.files[0]
            }));
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const formDataObj = new FormData();
            const jobData = {
                ...formData,
                deadline: formData.deadline ? formData.deadline.toISOString().split('T')[0] : '',
                banner: undefined // Exclude banner file from JSON
            };
            formDataObj.append('job', JSON.stringify(jobData));
            if (formData.banner instanceof File) {
                formDataObj.append('banner', formData.banner);
            }

            if (isCreateMode) {
                await api.post('/jobs', formDataObj, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await api.put(`/jobs/${id}`, formDataObj, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            navigate('/jobs');
        } catch (error) {
            console.error("Error saving job:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Bạn có chắc chắn muốn xóa công việc này?')) {
            try {
                await api.delete(`/jobs/${id}`);
                navigate('/jobs');
            } catch (error) {
                console.error("Error deleting job:", error);
            }
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image'
    ];

    return (
        <div className="job-detail">
            <div className="page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={() => navigate('/jobs')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <h1 className="page-title">{isCreateMode ? 'Tạo công việc mới' : 'Chi tiết công việc'}</h1>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    {!isCreateMode && (
                        <button
                            onClick={handleDelete}
                            style={{
                                background: '#FFEBEE',
                                border: 'none',
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                color: '#D32F2F',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Trash2 size={20} />
                        </button>
                    )}
                    <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Đang xử lý...' : 'Xác nhận'}
                    </button>
                </div>
            </div>

            <div className="form-section">
                <h3 className="section-title">Thông tin công việc</h3>
                <div className="form-group">
                    <label>Ảnh bìa</label>
                    <input
                        type="file"
                        className="form-input"
                        onChange={handleFileChange}
                        accept="image/*"
                    />
                    {formData.banner && typeof formData.banner === 'string' && (
                        <div style={{ marginTop: '8px' }}>
                            <img src={`${FILE_BASE_URL}${formData.banner}`} alt="Banner" style={{ maxHeight: '100px' }} />
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>Tên công ty</label>
                    <input
                        type="text"
                        className="form-input"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label>Tên công việc</label>
                    <input
                        type="text"
                        className="form-input"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div className="form-section">
                <h3 className="section-title">Chi tiết công việc</h3>
                <div className="form-grid-3">
                    <div className="form-group">
                        <label>Lĩnh vực</label>
                        <select
                            className="form-select"
                            name="field"
                            value={formData.field}
                            onChange={handleChange}
                        >
                            <option value="">Chọn lĩnh vực</option>
                            {categories.JOB_FIELD.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Địa điểm làm việc</label>
                        <select
                            className="form-select"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                        >
                            <option value="">Chọn địa điểm</option>
                            {categories.LOCATION.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Thời gian ứng tuyển</label>
                        <div className="date-input-wrapper">
                            <DatePicker
                                selected={formData.deadline}
                                onChange={handleDateChange}
                                dateFormat="dd/MM/yyyy"
                                className="form-input"
                                placeholderText="dd/mm/yyyy"
                            />
                            <Calendar className="input-icon-right" size={18} style={{ pointerEvents: 'none' }} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Vị trí</label>
                        <select
                            className="form-select"
                            name="position"
                            value={formData.position}
                            onChange={handleChange}
                        >
                            <option value="">Chọn vị trí</option>
                            {categories.POSITION.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Yêu cầu kinh nghiệm</label>
                        <select
                            className="form-select"
                            name="experience"
                            value={formData.experience}
                            onChange={handleChange}
                        >
                            <option value="">Chọn kinh nghiệm</option>
                            {categories.EXPERIENCE.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Trình độ học vấn</label>
                        <select
                            className="form-select"
                            name="education"
                            value={formData.education}
                            onChange={handleChange}
                        >
                            <option value="">Chọn trình độ</option>
                            {categories.EDUCATION.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Mức lương (từ): </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                className="form-input"
                                name="salary"
                                value={formData.salary}
                                onChange={handleChange}
                                placeholder="Nhập mức lương"
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Số lượng cần tuyển</label>
                        <input
                            type="number"
                            className="form-input"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>Hình thức làm việc</label>
                        <select
                            className="form-select"
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                        >
                            <option value="">Chọn hình thức</option>
                            {categories.JOB_TYPE.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Mức lương (đến): </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="text"
                                className="form-input"
                                name="salaryTo"
                                value={formData.salaryTo}
                                onChange={handleChange}
                                placeholder="Nhập mức lương"
                            />
                        </div>
                    </div>

                </div>
            </div>

            <div className="form-section">
                <h3 className="section-title">Mô tả công việc</h3>
                <div className="editor-placeholder">
                    <ReactQuill
                        theme="snow"
                        value={formData.description}
                        onChange={handleDescriptionChange}
                        modules={modules}
                        formats={formats}
                        className="editor-content"
                    />
                </div>
            </div>

            <div className="form-section">
                <h3 className="section-title">Yêu cầu công việc</h3>
                <div className="editor-placeholder">
                    <ReactQuill
                        theme="snow"
                        value={formData.requirements}
                        onChange={handleRequirementsChange}
                        modules={modules}
                        formats={formats}
                        className="editor-content"
                    />
                </div>
            </div>

            {!isCreateMode && (
                <div className="form-section">
                    <h3 className="section-title">Danh sách ứng viên</h3>
                    {applicants.length === 0 ? (
                        <p className="text-gray-500">Chưa có ứng viên nào ứng tuyển.</p>
                    ) : (
                        <>
                            <table className="applicants-table">
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Tên ứng viên</th>
                                        <th>Ngày sinh</th>
                                        <th>Số điện thoại</th>
                                        <th>Ngày nộp hồ sơ</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applicants.map((app, index) => {
                                        // Check if applied in last 3 days
                                        const isNew = () => {
                                            if (!app.appliedDate) return false;
                                            const appliedDate = new Date(app.appliedDate);
                                            const now = new Date();
                                            const diffTime = Math.abs(now - appliedDate);
                                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                            return diffDays <= 3;
                                        };

                                        return (
                                            <tr key={app.id}>
                                                <td>{index + 1}</td>
                                                <td>{app.profile?.name || 'N/A'}</td>
                                                <td>{app.profile?.dob || 'N/A'}</td>
                                                <td>{app.profile?.phone || 'N/A'}</td>
                                                <td>
                                                    {app.appliedDate}
                                                    {isNew() && <span className="badge-new">Mới</span>}
                                                </td>
                                                <td>
                                                    <a
                                                        href={`${API_BASE_URL}/profiles/${app.profile?.id}/download`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="download-link"
                                                    >
                                                        <Download size={16} /> Tải xuống hồ sơ
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="pagination">
                                <span>Hiển thị {applicants.length} trên 1 trang</span>
                            </div>
                        </>
                    )}
                </div>
            )}

            <style>{`
        .job-detail {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .form-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #E0E0E0;
        }
        .section-title {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
          color: #1A1A1A;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #1A1A1A;
        }
        .form-input, .form-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #E0E0E0;
          border-radius: 6px;
          font-size: 14px;
          background: #F5F7FA;
          box-sizing: border-box;
        }
        .form-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .date-input-wrapper {
          position: relative;
        }
        .date-input-wrapper .react-datepicker-wrapper {
            width: 100%;
        }
        .input-icon-right {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          z-index: 1;
        }
        .editor-placeholder {
          border: 1px solid #E0E0E0;
          border-radius: 6px;
          overflow: hidden;
          background: white;
        }
        .editor-content .ql-container {
            min-height: 150px;
            font-size: 14px;
        }
        .editor-content .ql-editor {
            min-height: 150px;
        }
        .applicants-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 16px;
        }
        .applicants-table thead {
            background-color: #D9E8F5;
        }
        .applicants-table th {
            padding: 12px 16px;
            text-align: left;
            font-size: 13px;
            font-weight: 600;
            color: #1A1A1A;
            border-bottom: 1px solid #E0E0E0;
        }
        .applicants-table tbody tr {
            border-bottom: 1px solid #F5F5F5;
        }
        .applicants-table tbody tr:hover {
            background-color: #F9FAFB;
        }
        .applicants-table td {
            padding: 12px 16px;
            font-size: 14px;
            color: #333;
        }
        .badge-new {
            display: inline-block;
            margin-left: 8px;
            padding: 2px 8px;
            background-color: #FFE5E5;
            color: #D32F2F;
            font-size: 12px;
            font-weight: 600;
            border-radius: 4px;
        }
        .download-link {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #1976D2;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
        }
        .download-link:hover {
            color: #1565C0;
            text-decoration: underline;
        }
        .pagination {
            margin-top: 16px;
            padding: 12px 0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            color: #666;
        }
      `}</style>
        </div>
    );
};

export default JobDetail;
